import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import api from "@/lib/api";
import { BookOpen, ExternalLink } from "lucide-react";

// Affiche le catalogue jeunesse (Graine de Fierté). Une seule source (/kids-books),
// deux présentations : "child" (ludique/visuelle) et "parent" (plus d'infos).
export default function KidsBookshelf({ variant = "child" }) {
  const [books, setBooks] = useState([]);

  useEffect(() => {
    let alive = true;
    api.get("/kids-books").then(({ data }) => { if (alive) setBooks(data.books || []); }).catch(() => {});
    return () => { alive = false; };
  }, []);

  if (!books.length) return null;

  const heading = variant === "parent" ? "Des livres pour vos enfants" : "Ma bibliothèque";

  return (
    <section className={variant === "parent" ? "mb-10" : "mt-8"} data-testid={`kids-bookshelf-${variant}`}>
      <div className="flex items-center gap-2 mb-4">
        <BookOpen size={20} className="text-gold" />
        <h2 className="font-display text-xl" style={{ color: "var(--text-main)" }}>{heading}</h2>
      </div>
      <div className={variant === "parent" ? "space-y-4" : "grid grid-cols-1 gap-4"}>
        {books.map((b, i) => (variant === "parent"
          ? <ParentCard key={b.id} book={b} index={i} />
          : <ChildCard key={b.id} book={b} index={i} />))}
      </div>
    </section>
  );
}

function ChildCard({ book, index }) {
  return (
    <motion.div initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: index * 0.08, duration: 0.5 }}
      data-testid={`kids-book-card-${book.id}`}
      className="rounded-3xl p-5 shadow-sm" style={{ background: "linear-gradient(135deg,#FFF3E0,#F3ECFF)" }}>
      <div className="flex gap-4">
        <img src={book.cover} alt={`${book.title} — ${book.subtitle}`}
          className="w-28 h-40 object-cover rounded-2xl shadow-md shrink-0" data-testid={`kids-book-cover-${book.id}`} />
        <div className="flex-1 min-w-0">
          <h3 className="font-display text-2xl leading-tight" style={{ color: "var(--accent-strong)" }}>{book.title}</h3>
          <p className="font-display text-base" style={{ color: "#B0742E" }}>{book.subtitle}</p>
          <div className="flex flex-wrap gap-1.5 mt-2">
            {book.tags.map((t) => (
              <span key={t} className="text-xs font-semibold rounded-full px-2.5 py-1" style={{ background: "#ffffffcc", color: "var(--accent-strong)" }}>{t}</span>
            ))}
          </div>
        </div>
      </div>
      <p className="text-base mt-3" style={{ color: "#3A3352" }}>{book.child_pitch}</p>
      <a href={book.url} target="_blank" rel="noopener noreferrer" data-testid={`kids-book-link-${book.id}`}
        className="mt-4 w-full rounded-2xl py-3.5 font-display text-lg text-white flex items-center justify-center gap-2 shadow-md transition-transform duration-200 hover:scale-[1.02] active:scale-95"
        style={{ background: "var(--accent-gradient)" }}>
        <BookOpen size={20} /> Découvrir le livre
      </a>
    </motion.div>
  );
}

function ParentCard({ book, index }) {
  return (
    <motion.div initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: index * 0.06, duration: 0.5 }}
      data-testid={`kids-book-card-${book.id}`}
      className="card-surface p-5 flex flex-col sm:flex-row gap-5">
      <img src={book.cover} alt={`${book.title} — ${book.subtitle}`}
        className="w-24 h-36 object-cover rounded-lg shadow-md shrink-0 self-center sm:self-start" data-testid={`kids-book-cover-${book.id}`} />
      <div className="flex-1">
        <h3 className="font-display text-lg" style={{ color: "var(--text-main)" }}>{book.title} — {book.subtitle}</h3>
        <p className="text-xs mt-0.5" style={{ color: "var(--accent)" }}>Collection {book.collection}</p>
        <p className="text-sm mt-2" style={{ color: "color-mix(in srgb, var(--text-main) 75%, transparent)" }}>{book.parent_pitch}</p>
        <p className="text-sm mt-2 font-medium" style={{ color: "var(--text-main)" }}>Âge recommandé : {book.age}</p>
        <a href={book.url} target="_blank" rel="noopener noreferrer" data-testid={`kids-book-link-${book.id}`}
          className="mt-4 inline-flex items-center gap-2 rounded-lg px-5 py-2.5 font-semibold transition-transform duration-150 hover:scale-105"
          style={{ background: "var(--accent-gradient)", color: "var(--accent-fg)" }}>
          Voir le livre sur Graine de Fierté <ExternalLink size={16} />
        </a>
      </div>
    </motion.div>
  );
}
